<template>
  <div v-if="refunds.length" class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-header pb-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
      <h5 class="mb-0">Remboursements déduits ({{ refunds.length }})</h5>
      <b class="text-danger">− {{ format(total) }}</b>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25">
      <p class="text-muted small mb-2">Montants remis aux destinataires avec les fonds d'encaissement du livreur (frais de service exclus).</p>
      <div class="table-responsive tr-scroll">
        <table class="table align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Demande</th>
              <th scope="col">Colis</th>
              <th scope="col">Destinataire</th>
              <th scope="col">Remboursé le</th>
              <th scope="col" class="text-end">Montant</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in refunds" :key="r.id">
              <td><b>{{ r.ref }}</b></td>
              <td>{{ r.parcel_code }}</td>
              <td>{{ r.receiver }}<small v-if="r.city_name" class="d-block text-muted">{{ r.city_name }}</small></td>
              <td>{{ formatDate(r.refunded_at) }}</td>
              <td class="text-end text-danger text-nowrap">− {{ format(r.amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import api from "@/utils/axios";
import { currency } from "@/utils/currency";
import moment from "moment";

interface Refund {
  id: number;
  ref: string;
  parcel_code: string;
  receiver: string | null;
  city_name: string | null;
  amount: string | number;
  refunded_at: string | null;
}

/** Refunds deducted from a driver's bon de paiement (see RefundPayNotes in the backend). */
export default defineComponent({
  name: "PaymentRefunds",
  props: {
    noteId: { type: Number, required: true },
  },
  setup(props) {
    const refunds = ref<Refund[]>([]);
    const total = computed(() => refunds.value.reduce((s, r) => s + (Number(r.amount) || 0), 0));

    async function fetchRefunds() {
      if (!props.noteId) return;
      try {
        const res = await api.get(`/api/auth/payment_note_refunds/${props.noteId}`);
        refunds.value = res.data.refunds ?? [];
      } catch {
        refunds.value = [];
      }
    }

    function format(value: unknown) {
      return (Number(value) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ` ${currency()}`;
    }
    function formatDate(value: string | null) {
      return value ? moment(value).format("YYYY-MM-DD HH:mm") : "—";
    }

    watch(() => props.noteId, fetchRefunds, { immediate: true });
    return { refunds, total, format, formatDate };
  },
});
</script>
